import { instantOf, minutesOf } from "./consolidationGeotabFill.js";
import { PAPER_TOLERANCE_MIN } from "./consolidationGeotabGantt.js";

// Soirée de chaque employé des préchargements (minutes depuis minuit de la veille au soir) : arrêts Geotab des camions
// qu'il a préchargés, chauffeur non connecté compris, et de ses autres camions quand il est connecté. Chaque préchargement
// est reconnu par ses commandes, comme un arrêt du relevé de jour.
// entries : { key, row } des préchargements ; data : réponse de /geotab/preloads.
export function preloadGeotabLanes(data, entries, defaultEmployeeId = null) {
  if (!data?.trucks) return [];
  const groups = new Map();
  for (const entry of entries) {
    const employee = entry.row.employee_id || defaultEmployeeId || "";
    groups.set(employee, [...(groups.get(employee) || []), entry]);
  }
  return [...groups].map(([employee, members]) => {
    const trucks = new Set(members.flatMap((entry) => entry.row.links.map((link) => link.truck_label)));
    const rows = data.trucks.filter((truck) => (truck.truck_id && trucks.has(truck.truck_id)) || (employee && truck.employee_id === employee));
    const matches = new Map(members.map((entry) => {
      const labels = new Set(entry.row.links.map((link) => link.label));
      const sites = rows.flatMap((truck) => truck.sites).filter((site) => site.real_arrival != null && site.order_ids?.some((id) => labels.has(id)));
      const ends = sites.map((site) => site.real_departure);
      return [entry.key, sites.length ? { start: Math.min(...sites.map((site) => site.real_arrival)), end: ends.includes(null) ? null : Math.max(...ends) } : null];
    }));
    return { key: employee || "none", employee, members, rows, matches, drivers: [...new Set(rows.flatMap((truck) => truck.drivers))] };
  });
}

// Colonne TransIA : le Dispatch ne fixe pas l'heure d'un préchargement ; chacun se place face à ses arrêts Geotab
// reconnus (from, to), sinon il est « Non détecté » (from null). Dernier arrêt sans départ connu : un trait (to = from).
export function preloadPlanStops(lane) {
  return lane.members.map(({ key, row }) => {
    const match = lane.matches.get(key);
    return { key, place: row.links[0]?.source_mill?.name || row.label || "Préchargement",
      trucks: [...new Set(row.links.map((link) => link.truck_number))].join(", "), orders: row.links.map((link) => link.label),
      tons: row.links.reduce((sum, link) => sum + (Number(link.planned_tons) || 0), 0),
      from: match?.start ?? null, to: match ? match.end ?? match.start : null };
  });
}

// « Quitté à » saisi (papier) face à Geotab : null si rien n'est saisi ou si Geotab ne connaît pas l'heure.
export function preloadPaper(value, geotab, evening, timezone) {
  const minutes = value && geotab != null ? minutesOf(value, { dispatch_date: evening, timezone }) : null;
  return minutes == null ? null : { minutes, gap: minutes - geotab, ok: Math.abs(minutes - geotab) <= PAPER_TOLERANCE_MIN };
}

// Heures Geotab de la soirée : début et fin du service (carnet de route de l'employé, sinon premier départ et dernier retour),
// départ et retour au dépôt de chaque camion préchargé, arrivée et « Quitté à » de chaque préchargement reconnu.
export function eveningGeotabFill(lane, data, employeeId, evening, timezone) {
  const at = (minutes) => instantOf(minutes, { dispatch_date: evening, timezone });
  const ids = new Map(lane.members.flatMap((entry) => entry.row.links.map((link) => [link.truck_label, link.truck_id])));
  const driven = lane.rows.filter((truck) => truck.visits.length);
  const trucks = {};
  for (const truck of driven) {
    if (!ids.has(truck.truck_id)) continue;
    const last = truck.visits.at(-1);
    trucks[ids.get(truck.truck_id)] = { departure: at(truck.departure ?? truck.visits[0].arr), return_at: last.kind === "depot" ? at(last.arr) : null };
  }
  const duty = data?.duty?.[employeeId];
  const first = Math.min(...driven.map((truck) => truck.departure ?? truck.visits[0].arr));
  const last = Math.max(...driven.map((truck) => truck.visits.at(-1).arr));
  const service = driven.length ? { service_start: at(duty?.start ?? first), service_end: at(duty?.end ?? last) } : {};
  const left = lane.members.filter((entry) => lane.matches.get(entry.key)?.end != null)
    .map((entry) => ({ key: entry.key, start_at: at(lane.matches.get(entry.key).start), end_at: at(lane.matches.get(entry.key).end), replaced: Boolean(entry.row.end_at) }));
  return { service, trucks, left, source: duty?.start != null ? "carnet de route" : "trajets des camions" };
}
